import React, { useCallback, useMemo, useState } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { GenerateVisualizationEvent } from 'libs/end-to-end/src/visualizer';
import { CustomVizDefinition } from 'libs/end-to-end/src/custom-viz-definition';
import { isCustomVizDefinitionCopilot } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { SlateView } from 'apps/sigma2-ui/src/copilot/components/SlateView';
import { SlateViewFilter } from 'apps/sigma2-ui/src/copilot/components/SlateView';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { BasicCopilotAddToSlateFn } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotAddToSlate';
import { BasicCopilotRemoveFromSlateFn } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotRemoveFromSlate';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';

export const CopilotSlateView = ({
  selectedSlateIds,
  setSelectedSlateIds,
  subjectsWithSlateStatus,
  addToSlate,
  removeFromSlate,
  isFetchingShareLink,
  onFetchShareLink,
  isShareable,
  nextSubjectIdToScrollTo,
  setShowGraphTooltip,
  currentlySelectedSubjectId,
  setCurrentlySelectedSubjectId,
  subjectIdsToPrefill,
  definition,
  focusArea,
}: {
  focusArea: FocusArea;
  definition: CustomVizDefinition;
  selectedSlateIds: string[];
  setSelectedSlateIds: Setter<string[]>;
  subjectsWithSlateStatus: SlatePoolSubject[];
  addToSlate: BasicCopilotAddToSlateFn;
  removeFromSlate: BasicCopilotRemoveFromSlateFn;
  isFetchingShareLink: boolean;
  onFetchShareLink: () => void;
  isShareable: boolean;
  nextSubjectIdToScrollTo: string | null;
  setShowGraphTooltip: Setter<boolean>;
  currentlySelectedSubjectId: string | null;
  setCurrentlySelectedSubjectId: Setter<string | null>;
  subjectIdsToPrefill: string[];
}) => {
  const { myUser } = useUser();

  const { myObservation } = useObservation();

  const { lastSuccessfulCustomVizRequest } = useCustomViz();

  const downloadableGenerateVisualizationEvent =
    useMemo<GenerateVisualizationEvent | null>(() => {
      const lastRunCustomVizRequestToExecute = lastSuccessfulCustomVizRequest;
      if (!lastRunCustomVizRequestToExecute) {
        return null;
      }

      if (
        !myUser ||
        !myUser?.organization?.id ||
        !myObservation ||
        !myUser.organization ||
        !isCustomVizDefinitionCopilot(definition)
      ) {
        return null;
      }

      const event: GenerateVisualizationEvent = {
        eventType: definition.customVizId,
        observationLocationType: myObservation.type,
        organizationId: myUser.organization.id,
        payload: {
          customVizRequest: lastRunCustomVizRequestToExecute,
          yourListSubjectIds: selectedSlateIds ?? [],
        },
      };

      return event;
    }, [
      lastSuccessfulCustomVizRequest,
      myUser,
      myObservation,
      definition,
      selectedSlateIds,
    ]);

  const [selectedSlateFilter, setSelectedSlateFilter] =
    useState<SlateViewFilter>('your-slate');

  const handleTapOnSlate = useCallback(
    (subjectId: string | null) => {
      setCurrentlySelectedSubjectId(subjectId);
      setShowGraphTooltip(true);
    },
    [setCurrentlySelectedSubjectId, setShowGraphTooltip],
  );

  const resetSlate = useCallback(() => {
    setSelectedSlateIds(subjectIdsToPrefill);
    setCurrentlySelectedSubjectId(null);
    setShowGraphTooltip(false);
  }, [
    setCurrentlySelectedSubjectId,
    setSelectedSlateIds,
    setShowGraphTooltip,
    subjectIdsToPrefill,
  ]);

  if (!myObservation) {
    return <></>;
  }

  return (
    <SlateView
      allSubjectsWithSlateStatus={subjectsWithSlateStatus}
      addToSlate={addToSlate}
      removeFromSlate={removeFromSlate}
      resetSlate={resetSlate}
      selectedSubjectId={currentlySelectedSubjectId}
      setSelectedSubjectId={handleTapOnSlate}
      selectedFilter={selectedSlateFilter}
      setSelectedFilter={setSelectedSlateFilter}
      nextSubjectIdToScrollTo={nextSubjectIdToScrollTo}
      focusArea={focusArea}
      downloadableGenerateVisualizationEvent={
        downloadableGenerateVisualizationEvent
      }
      isFetchingShareLink={isFetchingShareLink}
      onFetchShareLink={onFetchShareLink}
      isShareable={isShareable}
      calibrationFraming={myObservation.calibrationFraming}
    />
  );
};
